import { Fragment, useCallback, useEffect, useMemo, useState } from 'react'
import { Link } from 'react-router-dom'
import { useAuth } from '../auth/AuthProvider'
import { SortableTh } from '../components/SortableTh'
import { useTableSort } from '../hooks/useTableSort'
import {
  mesReferencia,
  pedidoTemAlocacao,
  ultimoDiaMesRef,
  valorTotalItens,
} from '../lib/pedidoMensal'
import {
  badgeSituacaoDivergencia,
  situacaoEntrega,
  SITUACAO_DIVERGENCIA_LABELS,
  type SituacaoDivergencia,
} from '../lib/entregaConformidade'
import { supabase } from '../lib/supabase'
import { downloadText, formatCurrency, formatDate, formatNumber, toCsv } from '../lib/utils'
import type { Compra, CompraItem, Entrega, EntregaItem, StatusValidacaoEntrega } from '../types/database'
import { STATUS_VALIDACAO_LABELS } from '../types/database'

type CompraItemRow = CompraItem & {
  alimento?: { nome: string; categoria?: string }
}

type CompraRow = Compra & {
  fornecedor?: { nome: string }
  registrado?: { nome: string }
  itens?: CompraItemRow[]
}

type EntregaRelatorio = Entrega & {
  escola?: { nome: string }
  rota?: { nome: string }
  itens?: Pick<EntregaItem, 'quantidade' | 'quantidade_planejada'>[]
  situacao: SituacaoDivergencia
}

function labelAlimento(categoria: string | undefined, nome: string) {
  if (!categoria) return nome
  return `[${categoria}] ${nome}`
}

function previstoPnae(item: CompraItemRow) {
  // null/undefined = sem previsto cadastrado → trata como 0 (fora do cardápio)
  if (item.quantidade_pnae == null) return 0
  return Number(item.quantidade_pnae) || 0
}

function itemAcimaDoPrevisto(item: CompraItemRow) {
  const pedida = Number(item.quantidade) || 0
  return pedida > previstoPnae(item)
}

export function AcompanhamentoPage() {
  const { canViewValores } = useAuth()
  const hoje = useMemo(() => new Date(), [])
  const [mesAno, setMesAno] = useState({ year: hoje.getFullYear(), month: hoje.getMonth() + 1 })
  const mesRef = mesReferencia(mesAno.year, mesAno.month)
  const mesInicio = mesRef
  const mesFim = ultimoDiaMesRef(mesRef)

  const [pedidos, setPedidos] = useState<CompraRow[]>([])
  const [entregas, setEntregas] = useState<EntregaRelatorio[]>([])
  const [expandedPedidoId, setExpandedPedidoId] = useState<string | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  const pedidosSort = useTableSort<CompraRow>('data', 'desc')
  const entregasSort = useTableSort<EntregaRelatorio>('situacao', 'asc')

  const load = useCallback(async () => {
    setLoading(true)
    setError(null)

    const [comprasRes, entregasRes] = await Promise.all([
      supabase
        .from('compras')
        .select(
          '*, fornecedor:fornecedores(nome), registrado:usuarios!compras_registrado_por_fkey(nome), itens:compra_itens(id, quantidade, quantidade_pnae, quantidade_alocada, valor_unitario, justificativa, alimento:alimentos_macronutrientes(nome, categoria))',
        )
        .eq('mes_referencia', mesRef)
        .order('numero', { ascending: true }),
      supabase
        .from('entregas')
        .select(
          '*, escola:escolas(nome), rota:rotas(nome), itens:entrega_itens(quantidade, quantidade_planejada)',
        )
        .gte('data', mesInicio)
        .lte('data', mesFim)
        .order('data', { ascending: false }),
    ])

    if (comprasRes.error || entregasRes.error) {
      setError(comprasRes.error?.message || entregasRes.error?.message || 'Erro ao carregar relatório')
      setLoading(false)
      return
    }

    const entregasRows = ((entregasRes.data || []) as Omit<EntregaRelatorio, 'situacao'>[]).map((e) => ({
      ...e,
      situacao: situacaoEntrega(e.itens),
    }))

    setPedidos((comprasRes.data as CompraRow[]) || [])
    setEntregas(entregasRows)
    setLoading(false)
  }, [mesRef, mesInicio, mesFim])

  useEffect(() => {
    document.title = 'Acompanhamento · ISEN EDUCAE'
    void load()
  }, [load])

  const kpis = useMemo(() => {
    const total = entregas.length
    const divergentes = entregas.filter((e) => e.situacao !== 'conforme').length
    const conformidade = total ? Math.round(((total - divergentes) / total) * 100) : 100
    return {
      pedidos: pedidos.length,
      entregas: total,
      divergentes,
      conformidade,
    }
  }, [pedidos, entregas])

  const pedidosSorted = useMemo(() => {
    const accessors: Record<string, (c: CompraRow) => string | number> = {
      codigo: (c) => c.codigo || '',
      data: (c) => c.data_compra,
      fornecedor: (c) => c.fornecedor?.nome || '',
      itens: (c) => c.itens?.length || 0,
      valor: (c) => valorTotalItens(c.itens || []),
      alocacao: (c) => (pedidoTemAlocacao(c.itens) ? 1 : 0),
    }
    return pedidosSort.sortRows(pedidos, accessors)
  }, [pedidos, pedidosSort])

  const entregasSorted = useMemo(() => {
    const accessors: Record<string, (e: EntregaRelatorio) => string | number> = {
      data: (e) => e.data,
      escola: (e) => e.escola?.nome || '',
      rota: (e) => e.rota?.nome || '',
      recebido: (e) => e.recebido_por || '',
      situacao: (e) => (e.situacao === 'conforme' ? 'z' : e.situacao),
      status: (e) => e.status_validacao || '',
    }
    const sorted = entregasSort.sortRows(entregas, accessors)
    if (entregasSort.sortKey === 'situacao' && entregasSort.sortDir === 'asc') {
      return [...sorted].sort((a, b) => {
        const wa = a.situacao === 'conforme' ? 1 : 0
        const wb = b.situacao === 'conforme' ? 1 : 0
        return wa - wb
      })
    }
    return sorted
  }, [entregas, entregasSort])

  const mesTitulo = new Date(mesAno.year, mesAno.month - 1, 1).toLocaleDateString('pt-BR', {
    month: 'long',
    year: 'numeric',
  })

  function exportCsv() {
    downloadText(
      `acompanhamento-entregas-${mesRef.slice(0, 7)}.csv`,
      toCsv(
        entregasSorted.map((e) => ({
          Data: e.data,
          Escola: e.escola?.nome || '',
          Rota: e.rota?.nome || '',
          'Recebido por': e.recebido_por || '',
          Situação: SITUACAO_DIVERGENCIA_LABELS[e.situacao],
          Motivo: e.justificativa_divergencia || '',
          Validação: STATUS_VALIDACAO_LABELS[e.status_validacao as StatusValidacaoEntrega] || e.status_validacao,
        })),
      ),
    )
  }

  const pedidosColCount = canViewValores ? 7 : 6
  const pedidoItemColCount = canViewValores ? 5 : 4

  if (loading) return <div className="loading">Carregando acompanhamento…</div>

  return (
    <div>
      <div className="page-header">
        <h1>Acompanhamento</h1>
        <div className="page-actions">
          <label className="field" style={{ margin: 0 }}>
            <span className="kpi-sub" style={{ marginRight: 8 }}>
              Mês
            </span>
            <input
              type="month"
              value={mesRef.slice(0, 7)}
              onChange={(e) => {
                const [y, m] = e.target.value.split('-').map(Number)
                if (y && m) setMesAno({ year: y, month: m })
              }}
            />
          </label>
          <button type="button" className="btn btn-secondary" onClick={() => void load()}>
            Atualizar
          </button>
          <button type="button" className="btn btn-secondary" onClick={exportCsv}>
            Exportar CSV
          </button>
        </div>
      </div>

      {error && <div className="alert alert-error">{error}</div>}

      <p className="kpi-sub" style={{ marginBottom: 12 }}>
        Relatório gerencial de pedidos e entregas —{' '}
        <strong style={{ textTransform: 'capitalize' }}>{mesTitulo}</strong>
      </p>

      <div className="kpi-grid">
        <div className="kpi">
          <div className="kpi-label">Pedidos no mês</div>
          <div className="kpi-value">{kpis.pedidos}</div>
        </div>
        <div className="kpi">
          <div className="kpi-label">Entregas no mês</div>
          <div className="kpi-value">{kpis.entregas}</div>
        </div>
        <div className="kpi">
          <div className="kpi-label">Com divergência</div>
          <div className="kpi-value">{kpis.divergentes}</div>
        </div>
        <div className="kpi">
          <div className="kpi-label">Conformidade</div>
          <div className="kpi-value">{kpis.conformidade}%</div>
        </div>
      </div>

      <div className="card" style={{ marginBottom: 16 }}>
        <div className="page-header" style={{ marginBottom: 12, padding: 0 }}>
          <h2 style={{ fontSize: 16, margin: 0 }}>Pedido mensal</h2>
          <Link to="/pedido-mensal" className="kpi-sub" style={{ marginLeft: 'auto' }}>
            Abrir Pedido Mensal
          </Link>
        </div>
        {!pedidosSorted.length ? (
          <div className="empty-state">Nenhum pedido neste mês.</div>
        ) : (
          <div className="table-wrap">
            <table className="data-table">
              <thead>
                <tr>
                  <th className="col-compact" />
                  <SortableTh
                    label="Código"
                    sortKey="codigo"
                    activeKey={pedidosSort.sortKey}
                    direction={pedidosSort.sortDir}
                    onSort={pedidosSort.toggleSort}
                    col="compact"
                  />
                  <SortableTh
                    label="Data"
                    sortKey="data"
                    activeKey={pedidosSort.sortKey}
                    direction={pedidosSort.sortDir}
                    onSort={pedidosSort.toggleSort}
                    col="date"
                  />
                  <SortableTh
                    label="Fornecedor"
                    sortKey="fornecedor"
                    activeKey={pedidosSort.sortKey}
                    direction={pedidosSort.sortDir}
                    onSort={pedidosSort.toggleSort}
                    col="text"
                  />
                  <SortableTh
                    label="Itens"
                    sortKey="itens"
                    activeKey={pedidosSort.sortKey}
                    direction={pedidosSort.sortDir}
                    onSort={pedidosSort.toggleSort}
                    col="num"
                  />
                  {canViewValores && (
                    <SortableTh
                      label="Valor"
                      sortKey="valor"
                      activeKey={pedidosSort.sortKey}
                      direction={pedidosSort.sortDir}
                      onSort={pedidosSort.toggleSort}
                      col="num"
                    />
                  )}
                  <SortableTh
                    label="Alocação"
                    sortKey="alocacao"
                    activeKey={pedidosSort.sortKey}
                    direction={pedidosSort.sortDir}
                    onSort={pedidosSort.toggleSort}
                    col="status"
                  />
                </tr>
              </thead>
              <tbody>
                {pedidosSorted.map((c) => {
                  const expanded = expandedPedidoId === c.id
                  return (
                    <Fragment key={c.id}>
                      <tr
                        className={expanded ? 'row-active' : undefined}
                        style={{ cursor: 'pointer' }}
                        onClick={() => setExpandedPedidoId(expanded ? null : c.id)}
                      >
                        <td className="col-compact">{expanded ? '▼' : '▶'}</td>
                        <td className="col-compact">{c.codigo}</td>
                        <td className="col-date">{formatDate(c.data_compra)}</td>
                        <td className="col-text">{c.fornecedor?.nome || '—'}</td>
                        <td className="col-num">{c.itens?.length || 0}</td>
                        {canViewValores && (
                          <td className="col-num">{formatCurrency(valorTotalItens(c.itens || []))}</td>
                        )}
                        <td className="col-status">
                          <span
                            className={
                              pedidoTemAlocacao(c.itens) ? 'badge badge-ok' : 'badge badge-neutral'
                            }
                          >
                            {pedidoTemAlocacao(c.itens) ? 'Com alocação' : 'Sem alocação'}
                          </span>
                        </td>
                      </tr>
                      {expanded && (
                        <tr>
                          <td colSpan={pedidosColCount} style={{ padding: 0 }}>
                            <div className="table-wrap" style={{ margin: '8px 12px 12px' }}>
                              <table className="data-table">
                                <thead>
                                  <tr>
                                    <th>Alimento</th>
                                    <th className="col-num">Previsto (PNAE)</th>
                                    <th className="col-num">Pedido</th>
                                    {canViewValores && <th className="col-num">Valor unit.</th>}
                                    <th>Justificativa</th>
                                  </tr>
                                </thead>
                                <tbody>
                                  {(c.itens || []).map((item) => {
                                    const acima = itemAcimaDoPrevisto(item)
                                    const justificativa = item.justificativa?.trim() || ''
                                    const destacar = acima || !!justificativa
                                    const nome = item.alimento?.nome || '—'
                                    return (
                                      <tr
                                        key={item.id}
                                        className={destacar ? 'row-warn' : undefined}
                                      >
                                        <td>{labelAlimento(item.alimento?.categoria, nome)}</td>
                                        <td className="col-num">
                                          {formatNumber(previstoPnae(item), 3)}
                                        </td>
                                        <td className="col-num">
                                          {formatNumber(Number(item.quantidade), 3)}
                                        </td>
                                        {canViewValores && (
                                          <td className="col-num">
                                            {formatCurrency(Number(item.valor_unitario))}
                                          </td>
                                        )}
                                        <td>{justificativa || '—'}</td>
                                      </tr>
                                    )
                                  })}
                                  {!c.itens?.length && (
                                    <tr>
                                      <td colSpan={pedidoItemColCount} className="kpi-sub">
                                        Nenhum item neste pedido.
                                      </td>
                                    </tr>
                                  )}
                                </tbody>
                              </table>
                            </div>
                          </td>
                        </tr>
                      )}
                    </Fragment>
                  )
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <div className="card">
        <div className="page-header" style={{ marginBottom: 12, padding: 0 }}>
          <h2 style={{ fontSize: 16, margin: 0 }}>Entregas e conformidade</h2>
          <Link to="/entregas" className="kpi-sub" style={{ marginLeft: 'auto' }}>
            Abrir Entregas
          </Link>
        </div>
        {!entregasSorted.length ? (
          <div className="empty-state">Nenhuma entrega neste mês.</div>
        ) : (
          <div className="table-wrap">
            <table className="data-table">
              <thead>
                <tr>
                  <SortableTh
                    label="Data"
                    sortKey="data"
                    activeKey={entregasSort.sortKey}
                    direction={entregasSort.sortDir}
                    onSort={entregasSort.toggleSort}
                    col="date"
                  />
                  <SortableTh
                    label="Escola"
                    sortKey="escola"
                    activeKey={entregasSort.sortKey}
                    direction={entregasSort.sortDir}
                    onSort={entregasSort.toggleSort}
                    col="name"
                  />
                  <SortableTh
                    label="Rota"
                    sortKey="rota"
                    activeKey={entregasSort.sortKey}
                    direction={entregasSort.sortDir}
                    onSort={entregasSort.toggleSort}
                    col="text"
                  />
                  <SortableTh
                    label="Recebido por"
                    sortKey="recebido"
                    activeKey={entregasSort.sortKey}
                    direction={entregasSort.sortDir}
                    onSort={entregasSort.toggleSort}
                    col="text"
                  />
                  <SortableTh
                    label="Situação"
                    sortKey="situacao"
                    activeKey={entregasSort.sortKey}
                    direction={entregasSort.sortDir}
                    onSort={entregasSort.toggleSort}
                    col="status"
                  />
                  <th className="col-text">Motivo</th>
                  <SortableTh
                    label="Validação"
                    sortKey="status"
                    activeKey={entregasSort.sortKey}
                    direction={entregasSort.sortDir}
                    onSort={entregasSort.toggleSort}
                    col="status"
                  />
                </tr>
              </thead>
              <tbody>
                {entregasSorted.map((e) => (
                  <tr key={e.id} className={e.situacao !== 'conforme' ? 'row-warn' : undefined}>
                    <td className="col-date">{formatDate(e.data)}</td>
                    <td className="col-name">{e.escola?.nome || '—'}</td>
                    <td className="col-text">{e.rota?.nome || '—'}</td>
                    <td className="col-text">{e.recebido_por || '—'}</td>
                    <td className="col-status">
                      <span className={badgeSituacaoDivergencia(e.situacao)}>
                        {SITUACAO_DIVERGENCIA_LABELS[e.situacao]}
                      </span>
                    </td>
                    <td className="col-text">{e.justificativa_divergencia || '—'}</td>
                    <td className="col-status">
                      {STATUS_VALIDACAO_LABELS[e.status_validacao] || e.status_validacao}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  )
}
